import React, { useEffect, useState } from 'react';
import { AlertTriangle, Bell, CheckCircle2, Ban, CreditCard } from 'lucide-react';
import { Link, useNavigate, Outlet, useLocation, useMatch } from 'react-router-dom';
import { useBranding } from '../../../context/BrandingContext';
import { fetchEnterpriseProfile, EnterpriseProfile, updateEnterpriseProfile, listTeamMembers, TeamMember, fetchCountries, fetchJurisdictionsByCountry, CountryOption, JurisdictionOption } from '../../../api/enterpriseApi';
import { fetchCurrentUser } from '../../../api/authApi';
import { useAuth } from '../../../context/AuthContext';
import OverviewPanel from '../../dashboard/components/OverviewPanel';
import { useBilling } from '../../../context/BillingContext';
import styles from './EnterpriseDashboardPage.module.css';
import { JurisdictionPicker } from '../components/JurisdictionPicker';
import { Eyebrow } from '../../../design-system';

const COUNTRY_ISO: Record<string, string> = {
  'india': 'IN', 'united states': 'US', 'usa': 'US', 'us': 'US', 'uk': 'GB',
  'united kingdom': 'GB', 'china': 'CN', 'germany': 'DE', 'france': 'FR',
  'japan': 'JP', 'singapore': 'SG', 'australia': 'AU', 'canada': 'CA',
  'brazil': 'BR', 'russia': 'RU', 'south korea': 'KR', 'korea': 'KR',
  'italy': 'IT', 'spain': 'ES', 'netherlands': 'NL', 'switzerland': 'CH',
  'sweden': 'SE', 'norway': 'NO', 'denmark': 'DK', 'finland': 'FI',
  'belgium': 'BE', 'austria': 'AT', 'poland': 'PL', 'portugal': 'PT',
  'mexico': 'MX', 'indonesia': 'ID', 'saudi arabia': 'SA', 'uae': 'AE',
  'united arab emirates': 'AE', 'south africa': 'ZA', 'nigeria': 'NG',
  'kenya': 'KE', 'egypt': 'EG', 'israel': 'IL', 'turkey': 'TR',
  'argentina': 'AR', 'colombia': 'CO', 'chile': 'CL', 'peru': 'PE',
  'malaysia': 'MY', 'thailand': 'TH', 'vietnam': 'VN', 'philippines': 'PH',
  'new zealand': 'NZ', 'ireland': 'IE', 'hong kong': 'HK', 'taiwan': 'TW',
  'european union': 'EU', 'eu': 'EU',
};

function getFlag(name: string): string {
  // Handle "Germany – Germany" or "India – India" format — take first part
  const base = name.split(/\s*[–—-]\s*/)[0].trim();
  const key = base.toLowerCase();
  const iso = COUNTRY_ISO[key];
  if (!iso) return '🌐';
  if (iso === 'EU') return '🇪🇺';
  return iso.split('').map(c => String.fromCodePoint(0x1F1E6 - 65 + c.charCodeAt(0))).join('');
}

function cleanJurisName(name: string): string {
  // "Germany – Germany" → "Germany"
  const parts = name.split(/\s*[–—-]\s*/);
  if (parts.length >= 2 && parts[0].trim().toLowerCase() === parts[1].trim().toLowerCase()) {
    return parts[0].trim();
  }
  return name;
}

const BUSINESS_MODELS = ['B2B', 'B2C', 'B2B2C', 'D2C', 'Marketplace', 'SaaS', 'Other'];
const HIERARCHY_TYPES = ['Subsidiary', 'Affiliate', 'Joint Venture', 'Branch', 'Associate'];
const INDUSTRY_TYPES = [
  'Technology', 'Finance & Banking', 'Healthcare', 'Legal & Compliance',
  'Manufacturing', 'Retail & E-commerce', 'Education', 'Real Estate',
  'Energy & Utilities', 'Media & Entertainment', 'Consulting', 'Hospitality',
  'Logistics & Transport', 'Other',
];

type Section = 'overview' | 'search' | 'chat' | 'chat-history' | 'search-history' | 'company-profile' | 'repository' | 'governance' | 'risk' | 'compliance' | 'comparison' | 'regulations' | 'newsletters' | 'charts' | 'map' | 'team' | 'folders' | 'guide' | 'delete-account';
type MemberFeatureKey = 'overview' | 'search' | 'chat' | 'chat-history' | 'search-history' | 'regulations' | 'newsletters' | 'charts' | 'map' | 'comparison' | 'asset-repository' | 'governance' | 'team';

const SECTION_TO_FEATURE: Partial<Record<Section, MemberFeatureKey>> = {
  overview: 'overview',
  search: 'search',
  chat: 'chat',
  'chat-history': 'chat-history',
  'search-history': 'search-history',
  regulations: 'regulations',
  newsletters: 'newsletters',
  charts: 'charts',
  map: 'map',
  comparison: 'comparison',
  repository: 'asset-repository',
  governance: 'governance',
  risk: 'governance',
  compliance: 'governance',
  team: 'team',
  folders: 'regulations',
};

// howToUse steps from the User Guide, keyed by section path


const SECTION_PATHS: Record<Section, string> = {
  overview: '/enterprise/dashboard',
  search: '/enterprise/dashboard/search',
  chat: '/enterprise/dashboard/chat',
  'chat-history': '/enterprise/dashboard/chat-history',
  'search-history': '/enterprise/dashboard/search-history',
  'company-profile': '/enterprise/dashboard/company-profile',
  repository: '/enterprise/dashboard/repository',
  governance: '/enterprise/dashboard/governance',
  risk: '/enterprise/dashboard/risk',
  compliance: '/enterprise/dashboard/compliance',
  comparison: '/enterprise/dashboard/comparison',
  regulations: '/enterprise/dashboard/regulations',
  newsletters: '/enterprise/dashboard/newsletters',
  charts: '/enterprise/dashboard/charts',
  map: '/enterprise/dashboard/map',
  team: '/enterprise/dashboard/team',
  folders: '/enterprise/dashboard/folders',
  guide: '/enterprise/dashboard/guide',
  'delete-account': '/enterprise/dashboard/delete-account',
};

export default function EnterpriseDashboardPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isExactDashboard = useMatch('/enterprise/dashboard');
  const [profile, setProfile] = useState<EnterpriseProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);

  const section: Section = location.pathname.includes('/search-history')
    ? 'search-history'
    : location.pathname.includes('/chat-history')
    ? 'chat-history'
    : location.pathname.includes('/company-profile')
    ? 'company-profile'
    : location.pathname.includes('/repository')
    ? 'repository'
    : location.pathname.includes('/governance')
    ? 'governance'
    : location.pathname.includes('/risk')
    ? 'risk'
    : location.pathname.includes('/compliance')
    ? 'compliance'
    : location.pathname.includes('/comparison')
    ? 'comparison'
    : location.pathname.includes('/search')
    ? 'search'
    : location.pathname.includes('/chat')
    ? 'chat'
    : location.pathname.includes('/regulations-breakdown')
    ? 'charts'
    : location.pathname.includes('/regulations')
    ? 'regulations'
    : location.pathname.includes('/newsletters')
    ? 'newsletters'
    : location.pathname.includes('/charts')
    ? 'charts'
    : location.pathname.includes('/map')
    ? 'map'
    : location.pathname.includes('/team')
    ? 'team'
    : location.pathname.includes('/folders')
    ? 'folders'
    : location.pathname.includes('/guide')
    ? 'guide'
    : location.pathname.includes('/delete-account')
    ? 'delete-account'
    : 'overview';

  const isEnterpriseMember = Boolean(user?.is_enterprise_member);
  const allowedFeatureSet = React.useMemo(
    () => new Set((user?.enterprise_allowed_features ?? []).map((feature) => feature.toLowerCase())),
    [user?.enterprise_allowed_features]
  );

  const canAccessSection = React.useCallback((nextSection: Section): boolean => {
    if (!isEnterpriseMember) return true;
    if (nextSection === 'guide' || nextSection === 'delete-account') return true;
    const requiredFeature = SECTION_TO_FEATURE[nextSection];
    return requiredFeature ? allowedFeatureSet.has(requiredFeature) : false;
  }, [allowedFeatureSet, isEnterpriseMember]);

  const defaultAllowedPath = React.useMemo(() => {
    if (!isEnterpriseMember) return SECTION_PATHS.overview;
    const preferredOrder: Section[] = ['overview', 'search', 'chat', 'chat-history', 'search-history', 'regulations', 'newsletters', 'charts', 'map', 'comparison', 'repository', 'governance', 'risk', 'compliance'];
    const firstAllowed = preferredOrder.find((candidate) => canAccessSection(candidate));
    return firstAllowed ? SECTION_PATHS[firstAllowed] : SECTION_PATHS.overview;
  }, [canAccessSection, isEnterpriseMember]);

  useEffect(() => {
    let cancelled = false;

    fetchEnterpriseProfile()
      .catch(() => null)
      .then((nextProfile) => {
        if (cancelled) return;
        setProfile(nextProfile);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!loading && isEnterpriseMember && !canAccessSection(section)) {
      navigate(defaultAllowedPath, { replace: true });
    }
  }, [loading, isEnterpriseMember, canAccessSection, section, defaultAllowedPath, navigate]);

  // Poll for feature access changes every 30s so members are redirected in real-time
  // when the enterprise admin updates their allowed features.
  useEffect(() => {
    if (!isEnterpriseMember) return;
    const interval = setInterval(async () => {
      try {
        const storedToken = window.localStorage.getItem('policyora_token');
        if (!storedToken) return;
        const freshUser = await fetchCurrentUser(storedToken);
        updateUser(freshUser);
      } catch {
        // silently ignore — next poll will retry
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [isEnterpriseMember, updateUser]);

  return (
    <>

        {isExactDashboard && !loading && (
          <OverviewSection profile={profile} user={user} />
        )}
        {!isExactDashboard && (
          <Outlet context={{ selectedSessionId, setSelectedSessionId, refreshSessions: () => {} }} />
        )}
    </>
  );
}

function OverviewSection({ profile: initialProfile, user }: { profile: EnterpriseProfile | null; user: any }) {
  const { subscription } = useBilling();
  const [overviewMembers, setOverviewMembers] = useState<TeamMember[]>([]);
  useEffect(() => { listTeamMembers().then(setOverviewMembers).catch(() => {}); }, []);
  const subEnd = subscription?.current_period_end ? new Date(subscription.current_period_end) : null;
  const daysUntilExpiry = subEnd ? Math.ceil((subEnd.getTime() - Date.now()) / 86400000) : null;
  const seatsUsed = overviewMembers.filter(m => m.is_active).length;
  const contactEmail = 'enterprise@policyora.com';

  const [profile, setProfile] = useState<EnterpriseProfile | null>(initialProfile);
  const [bizEditing, setBizEditing] = useState(false);
  const [bizModel, setBizModel] = useState('');
  const [bizParent, setBizParent] = useState('');
  const [bizSubsidiaries, setBizSubsidiaries] = useState<string[]>([]);
  const [bizSubInput, setBizSubInput] = useState('');
  const [bizHierarchy, setBizHierarchy] = useState<{ name: string; type: string }[]>([]);
  const [bizNodeName, setBizNodeName] = useState('');
  const [bizNodeType, setBizNodeType] = useState('Subsidiary');
  const [bizSaving, setBizSaving] = useState(false);
  const [infoEditing, setInfoEditing] = useState(false);
  const [infoName, setInfoName] = useState('');
  const [infoJurisdiction, setInfoJurisdiction] = useState('');
  const [infoYears, setInfoYears] = useState('');
  const [infoMembers, setInfoMembers] = useState('');
  const [infoProducts, setInfoProducts] = useState('');
  const [infoIndustry, setInfoIndustry] = useState('');
  const [infoSaving, setInfoSaving] = useState(false);

  // Jurisdiction picker state
  const [jurisCountries, setJurisCountries] = useState<CountryOption[]>([]);
  const [jurisSubList, setJurisSubList] = useState<JurisdictionOption[]>([]);
  const [jurisSubLoading, setJurisSubLoading] = useState(false);
  const [jurisCountryId, setJurisCountryId] = useState('');
  const [jurisCountryName, setJurisCountryName] = useState('');
  const [jurisSubId, setJurisSubId] = useState('');
  const [, setJurisSubName] = useState('');
  const [jurisOtherCountry, setJurisOtherCountry] = useState(false);
  const [jurisOtherSub, setJurisOtherSub] = useState(false);
  const [jurisCustomCountry, setJurisCustomCountry] = useState('');
  const [jurisCustomSub, setJurisCustomSub] = useState('');

  useEffect(() => {
    fetchCountries().then(setJurisCountries).catch(() => {});
  }, []);

  const handleJurisCountryChange = async (countryId: string, countryName: string) => {
    if (countryId === '__other__') {
      setJurisOtherCountry(true);
      setJurisCountryId('__other__');
      setJurisCountryName('');
      setJurisSubList([]);
      setJurisSubId('');
      setJurisSubName('');
      setJurisOtherSub(false);
      return;
    }
    setJurisOtherCountry(false);
    setJurisCountryId(countryId);
    setJurisCountryName(countryName);
    setJurisSubId('');
    setJurisSubName('');
    setJurisOtherSub(false);
    setJurisSubList([]);
    // sync display value immediately
    setInfoJurisdiction(countryName);
    if (!countryId) return;
    setJurisSubLoading(true);
    try {
      const subs = await fetchJurisdictionsByCountry(countryId);
      setJurisSubList(subs);
    } catch { /* ignore */ }
    finally { setJurisSubLoading(false); }
  };

  const handleJurisSubChange = (id: string, name: string) => {
    if (id === '__other__') {
      setJurisOtherSub(true);
      setJurisSubId('__other__');
      setJurisSubName('');
    } else {
      setJurisOtherSub(false);
      setJurisSubId(id);
      setJurisSubName(name);
      const resolved = [jurisCountryName, name].filter(Boolean).join(' – ');
      setInfoJurisdiction(resolved);
    }
  };

  // Company logo: stored server-side per workspace (SRS #38) — the previous
  // localStorage copy never reached reports or other team members.
  const { logo: companyLogo, brand } = useBranding();
  const [activeOverviewTab, setActiveOverviewTab] = useState<'overview' | 'business' | 'compliance' | 'contacts' | 'account' | 'notifications'>('overview');

  const [regEditing, setRegEditing] = useState(false);
  const [regMacId, setRegMacId] = useState('');
  const [regGst, setRegGst] = useState('');
  const [regSaving, setRegSaving] = useState(false);

  const [foundersEditing, setFoundersEditing] = useState(false);
  const [foundersList, setFoundersList] = useState<string[]>([]);
  const [foundersInput, setFoundersInput] = useState('');
  const [foundersSaving, setFoundersSaving] = useState(false);

  const [jurisEditing, setJurisEditing] = useState(false);
  const [jurisActive, setJurisActive] = useState<string[]>([]);
  const [jurisPlanned, setJurisPlanned] = useState<string[]>([]);
  const [jurisSaving, setJurisSaving] = useState(false);

  // Consolidated company profile fields
  const [infoCompanySize, setInfoCompanySize] = useState('');
  const [infoHeadquarters, setInfoHeadquarters] = useState('');
  const [infoAiUsage, setInfoAiUsage] = useState<string[]>([]);
  const [infoProcessesPersonalData, setInfoProcessesPersonalData] = useState<boolean | null>(null);
  const [infoRegulatoryDeclarations, setInfoRegulatoryDeclarations] = useState<string[]>([]);
  const [infoContacts, setInfoContacts] = useState<{ role: string; name: string; email: string; title: string }[]>([]);

  useEffect(() => { setProfile(initialProfile); }, [initialProfile]);

  const openInfoEdit = () => {
    setInfoName(profile?.company_name || '');
    const existingJuris = profile?.jurisdiction || '';
    setInfoJurisdiction(existingJuris);
    // Reset jurisdiction picker — treat existing value as custom fallback
    setJurisCountryId('');
    setJurisCountryName('');
    setJurisSubId('');
    setJurisSubName('');
    setJurisSubList([]);
    setJurisOtherCountry(false);
    setJurisOtherSub(false);
    setJurisCustomCountry('');
    setJurisCustomSub('');
    setInfoYears(profile?.years_in_operation || '');
    setInfoMembers(profile?.num_members || '');
    setInfoProducts(profile?.products_services || '');
    setInfoIndustry(profile?.industry_type || '');
    setInfoCompanySize(profile?.company_size || '');
    setInfoHeadquarters(profile?.headquarters_location || '');
    setInfoAiUsage(profile?.ai_usage_declaration || []);
    setInfoProcessesPersonalData(profile?.processes_personal_data ?? null);
    setInfoRegulatoryDeclarations(profile?.regulatory_declarations || []);
    setInfoContacts((profile?.contacts || []).map(c => ({ role: c.role, name: c.name, email: c.email, title: c.title || '' })));
    setInfoEditing(true);
  };

  const saveInfoEdit = async () => {
    if (!profile) return;
    setInfoSaving(true);
    try {
      const updated = await updateEnterpriseProfile({
        company_name: infoName || profile.company_name || '',
        jurisdiction: infoJurisdiction || profile.jurisdiction || '',
        years_in_operation: infoYears || profile.years_in_operation || '',
        mac_id: profile.mac_id || undefined,
        gst_number: profile.gst_number || undefined,
        founder_names: profile.founder_names || undefined,
        num_members: infoMembers || profile.num_members || undefined,
        existing_policies: profile.existing_policies || undefined,
        products_services: infoProducts || profile.products_services || undefined,
        other_jurisdictions: profile.other_jurisdictions || undefined,
        new_jurisdictions: profile.new_jurisdictions || undefined,
        business_model: profile.business_model || undefined,
        industry_type: infoIndustry || undefined,
        parent_company: profile.parent_company || undefined,
        subsidiary_companies: profile.subsidiary_companies?.length ? profile.subsidiary_companies : undefined,
        company_hierarchy: profile.company_hierarchy?.length ? profile.company_hierarchy : undefined,
        company_size: infoCompanySize || undefined,
        headquarters_location: infoHeadquarters || undefined,
        ai_usage_declaration: infoAiUsage.length ? infoAiUsage : undefined,
        processes_personal_data: infoProcessesPersonalData ?? undefined,
        regulatory_declarations: infoRegulatoryDeclarations.length ? infoRegulatoryDeclarations : undefined,
        contacts: infoContacts.length ? infoContacts : undefined,
      });
      setProfile(updated);
      setInfoEditing(false);
    } catch { /* ignore */ }
    finally { setInfoSaving(false); }
  };

  const openBizEdit = () => {
    setBizModel(profile?.business_model || '');
    setBizParent(profile?.parent_company || '');
    setBizSubsidiaries(profile?.subsidiary_companies || []);
    setBizHierarchy(profile?.company_hierarchy || []);
    setBizEditing(true);
  };

  const saveBizEdit = async () => {
    if (!profile) return;
    setBizSaving(true);
    try {
      const updated = await updateEnterpriseProfile({
        company_name: profile.company_name || '',
        jurisdiction: profile.jurisdiction || '',
        years_in_operation: profile.years_in_operation || '',
        mac_id: profile.mac_id || undefined,
        gst_number: profile.gst_number || undefined,
        founder_names: profile.founder_names || undefined,
        num_members: profile.num_members || undefined,
        existing_policies: profile.existing_policies || undefined,
        products_services: profile.products_services || undefined,
        other_jurisdictions: profile.other_jurisdictions || undefined,
        new_jurisdictions: profile.new_jurisdictions || undefined,
        business_model: bizModel || undefined,
        industry_type: profile.industry_type || undefined,
        parent_company: bizParent || undefined,
        subsidiary_companies: bizSubsidiaries.length ? bizSubsidiaries : undefined,
        company_hierarchy: bizHierarchy.length ? bizHierarchy : undefined,
      });
      setProfile(updated);
      setBizEditing(false);
    } catch { /* ignore */ }
    finally { setBizSaving(false); }
  };

  const openRegEdit = () => {
    setRegMacId(profile?.mac_id || '');
    setRegGst(profile?.gst_number || '');
    setRegEditing(true);
  };

  const saveRegEdit = async () => {
    if (!profile) return;
    setRegSaving(true);
    try {
      const updated = await updateEnterpriseProfile({
        company_name: profile.company_name || '',
        jurisdiction: profile.jurisdiction || '',
        years_in_operation: profile.years_in_operation || '',
        mac_id: regMacId || undefined,
        gst_number: regGst || undefined,
        founder_names: profile.founder_names || undefined,
        num_members: profile.num_members || undefined,
        existing_policies: profile.existing_policies || undefined,
        products_services: profile.products_services || undefined,
        other_jurisdictions: profile.other_jurisdictions || undefined,
        new_jurisdictions: profile.new_jurisdictions || undefined,
        business_model: profile.business_model || undefined,
        industry_type: profile.industry_type || undefined,
        parent_company: profile.parent_company || undefined,
        subsidiary_companies: profile.subsidiary_companies?.length ? profile.subsidiary_companies : undefined,
        company_hierarchy: profile.company_hierarchy?.length ? profile.company_hierarchy : undefined,
      });
      setProfile(updated);
      setRegEditing(false);
    } catch { /* ignore */ }
    finally { setRegSaving(false); }
  };

  const openFoundersEdit = () => {
    setFoundersList(profile?.founder_names || []);
    setFoundersInput('');
    setFoundersEditing(true);
  };

  const saveFoundersEdit = async () => {
    if (!profile) return;
    setFoundersSaving(true);
    try {
      const updated = await updateEnterpriseProfile({
        company_name: profile.company_name || '', jurisdiction: profile.jurisdiction || '',
        years_in_operation: profile.years_in_operation || '', mac_id: profile.mac_id || undefined,
        gst_number: profile.gst_number || undefined, founder_names: foundersList.length ? foundersList : undefined,
        num_members: profile.num_members || undefined, existing_policies: profile.existing_policies || undefined,
        products_services: profile.products_services || undefined, other_jurisdictions: profile.other_jurisdictions || undefined,
        new_jurisdictions: profile.new_jurisdictions || undefined, business_model: profile.business_model || undefined,
        industry_type: profile.industry_type || undefined, parent_company: profile.parent_company || undefined,
        subsidiary_companies: profile.subsidiary_companies?.length ? profile.subsidiary_companies : undefined,
        company_hierarchy: profile.company_hierarchy?.length ? profile.company_hierarchy : undefined,
      });
      setProfile(updated);
      setFoundersEditing(false);
    } catch { /* ignore */ } finally { setFoundersSaving(false); }
  };

  const openJurisEdit = () => {
    setJurisActive(profile?.other_jurisdictions || []);
    setJurisPlanned(profile?.new_jurisdictions || []);
    setJurisEditing(true);
  };

  const saveJurisEdit = async () => {
    if (!profile) return;
    setJurisSaving(true);
    try {
      const updated = await updateEnterpriseProfile({
        company_name: profile.company_name || '', jurisdiction: profile.jurisdiction || '',
        years_in_operation: profile.years_in_operation || '', mac_id: profile.mac_id || undefined,
        gst_number: profile.gst_number || undefined, founder_names: profile.founder_names || undefined,
        num_members: profile.num_members || undefined, existing_policies: profile.existing_policies || undefined,
        products_services: profile.products_services || undefined,
        other_jurisdictions: jurisActive.length ? jurisActive : undefined,
        new_jurisdictions: jurisPlanned.length ? jurisPlanned : undefined,
        business_model: profile.business_model || undefined, industry_type: profile.industry_type || undefined,
        parent_company: profile.parent_company || undefined,
        subsidiary_companies: profile.subsidiary_companies?.length ? profile.subsidiary_companies : undefined,
        company_hierarchy: profile.company_hierarchy?.length ? profile.company_hierarchy : undefined,
      });
      setProfile(updated);
      setJurisEditing(false);
    } catch { /* ignore */ } finally { setJurisSaving(false); }
  };

  if (!profile) {
    return <div className={styles.emptyState}><p>No enterprise profile found.</p></div>;
  }

  const allJurisdictions = [
    ...(profile.other_jurisdictions || []).map(j => ({ label: 'Active', value: j })),
    ...(profile.new_jurisdictions || []).map(j => ({ label: 'Planned', value: j })),
  ];

  const overviewTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'business', label: 'Business' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'contacts', label: 'Contacts' },
    { id: 'account', label: 'Account Details' },
    { id: 'notifications', label: 'Notifications' },
  ] as const;

  const overviewStats = [
    {
      label: 'Years Active', value: profile.years_in_operation ? `${profile.years_in_operation} yrs` : '—',
      color: 'var(--ds-link)', bg: 'var(--ds-info-bg)',
      icon: <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>,
    },
    {
      label: 'Employees', value: profile.num_members || '—',
      color: 'var(--ds-success-text)', bg: 'var(--ds-success-bg)',
      icon: <><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/></>,
    },
    {
      label: 'Company Size', value: profile.company_size || '—',
      color: 'var(--ds-link)', bg: 'var(--ds-info-bg)',
      icon: <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd"/>,
    },
    {
      label: 'Jurisdictions', value: allJurisdictions.length ? String(allJurisdictions.length) : '—',
      color: 'var(--ds-link)', bg: 'var(--ds-info-bg)',
      icon: <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd"/>,
    },
  ];

  const profileCompletionFields = [
    profile.company_name, profile.industry_type, profile.jurisdiction,
    profile.years_in_operation, profile.num_members, profile.business_model,
    profile.products_services, profile.headquarters_location, profile.mac_id, profile.gst_number,
  ];
  const completionPct = Math.round((profileCompletionFields.filter(Boolean).length / profileCompletionFields.length) * 100);
  const completionFieldSummary = 'Based on: Company name, Industry, Primary jurisdiction, Years in operation, Employees, Business model, Products & services, Headquarters, MAC ID, and GST number.';

  const profileSnapshot = [
    { label: 'Industry', value: profile.industry_type || '—' },
    {
      label: 'Primary jurisdiction',
      value: profile.jurisdiction ? `${getFlag(profile.jurisdiction)} ${cleanJurisName(profile.jurisdiction)}` : '—',
    },
    { label: 'Headquarters', value: profile.headquarters_location || '—' },
    { label: 'Business model', value: profile.business_model || '—' },
    {
      label: 'Data handling',
      value:
        profile.processes_personal_data === null || profile.processes_personal_data === undefined
          ? '—'
          : profile.processes_personal_data
            ? 'Processes personal data'
            : 'No personal data declared',
    },
  ];

  return (
    <div className={styles.overview}>
      <OverviewPanel token={typeof window !== 'undefined' ? window.localStorage.getItem('policyora_token') : null} />
      <div className={styles.profileSections}>

        {/* Company Information - Consolidated */}
        <div className={styles.section}>
          <div style={{ marginBottom: '0.6rem' }}>
            <Eyebrow tone="default" dot>COMPANY PROFILE</Eyebrow>
          </div>
          <div className={styles.companyLogoSection}>
            <div className={styles.companyInfoHeader}>
              <div className={styles.companyDetails}>
                <div className={styles.companyNameRow}>
                  <h1 className={styles.heroCompanyName}>{profile.company_name || 'Your Company'}</h1>
                  <span className={styles.completionBadge} title={`${completionPct}% of profile fields filled. ${completionFieldSummary}`}>
                    {completionPct}% complete
                  </span>
                </div>
                <p className={styles.completionHint}>{completionFieldSummary}</p>
                {profile.industry_type && (
                  <div className={styles.industryRow}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" className={styles.industryIcon}>
                      <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
                    </svg>
                    <span className={styles.industryName}>{profile.industry_type}</span>
                  </div>
                )}
                {profile.founder_names && profile.founder_names.length > 0 && (
                  <div className={styles.founderRow}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" className={styles.founderIcon}>
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                    </svg>
                    <p className={styles.founderName}>Founded by {profile.founder_names.join(', ')}</p>
                  </div>
                )}
                {(profile.jurisdiction || (profile.other_jurisdictions && profile.other_jurisdictions.length > 0)) && (
                  <div className={styles.locationsList}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" className={styles.locationIcon}>
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                    </svg>
                    <div className={styles.locationItems}>
                      {profile.jurisdiction && (
                        <span className={styles.locationBullet}>{cleanJurisName(profile.jurisdiction)}</span>
                      )}
                      {profile.other_jurisdictions && profile.other_jurisdictions.map((juris, i) => (
                        <span key={i} className={styles.locationBullet}>{cleanJurisName(juris)}</span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Stat chips row */}
                <div className={styles.headerStats}>
                  {profile.years_in_operation && (
                    <span className={styles.headerStatChip}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/></svg>
                      {profile.years_in_operation} yrs
                    </span>
                  )}
                  {profile.num_members && (
                    <span className={styles.headerStatChip}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/></svg>
                      {profile.num_members} members
                    </span>
                  )}
                  {profile.company_size && (
                    <span className={styles.headerStatChip}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd"/></svg>
                      {profile.company_size}
                    </span>
                  )}
                  {profile.business_model && (
                    <span className={styles.headerStatChip}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/></svg>
                      {profile.business_model}
                    </span>
                  )}
                  {allJurisdictions.length > 0 && (
                    <span className={styles.headerStatChip}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd"/></svg>
                      {allJurisdictions.length} jurisdiction{allJurisdictions.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.logoUploadArea}>
                {companyLogo ? (
                  <Link to="/settings/branding" className={styles.logoDisplay} title="Change in Settings › Branding">
                    <img src={companyLogo} alt={brand?.brand_name || 'Company logo'} className={styles.logoImage} />
                    <div className={styles.logoOverlay}>
                      <span className={styles.changeText}>Change logo</span>
                    </div>
                  </Link>
                ) : (
                  <Link to="/settings/branding" className={styles.logoPlaceholder}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="32" height="32" className={styles.logoIcon}>
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                    </svg>
                    <span className={styles.logoText}>Company Logo</span>
                    <span className={styles.logoSubtext}>Set in Settings › Branding</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
          <div className={styles.cardSectionHeader}>
            {!infoEditing && !bizEditing && !regEditing && !foundersEditing && !jurisEditing ? (
              <button className={styles.sectionEditBtn} onClick={() => {
                openInfoEdit();
                openBizEdit();
                openRegEdit();
                openFoundersEdit();
                openJurisEdit();
              }}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="11" height="11"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zm-2.207 2.207L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                <span>Edit</span>
              </button>
            ) : (
              <button className={styles.sectionCancelBtn} onClick={() => {
                setInfoEditing(false);
                setBizEditing(false);
                setRegEditing(false);
                setFoundersEditing(false);
                setJurisEditing(false);
              }}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="11" height="11"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                <span>Cancel editing</span>
              </button>
            )}
          </div>
          
          {!infoEditing && !bizEditing && !regEditing && !foundersEditing && !jurisEditing ? (
            <div className={styles.consolidatedDisplayForm}>
              <div className={styles.overviewNavRow}>
                {overviewTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`${styles.overviewNavItem} ${activeOverviewTab === tab.id ? styles.overviewNavItemActive : ''}`}
                    onClick={() => setActiveOverviewTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className={styles.profileDashboardGrid}>
                <div className={styles.profileDashboardMain}>
                  {activeOverviewTab === 'overview' && (
                    <div className={styles.overviewStatsGrid}>
                      {overviewStats.map((item) => (
                        <div key={item.label} className={styles.overviewStatCard}>
                          <div className={styles.statIconWrap} style={{ background: item.bg }}>
                            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" style={{ color: item.color }}>
                              {item.icon}
                            </svg>
                          </div>
                          <span className={styles.overviewStatLabel}>{item.label}</span>
                          <strong className={styles.overviewStatValue}>{item.value}</strong>
                        </div>
                      ))}
                    </div>
                  )}

                  {(activeOverviewTab === 'overview' || activeOverviewTab === 'business') && (
                    <div className={styles.overviewFeatureCard}>
                      <span className={styles.overviewEyebrow}>About</span>
                      <h3>{profile.company_name || 'Your Company'}</h3>
                      <p className={styles.overviewFeatureText}>{profile.products_services || 'No company summary added yet.'}</p>
                      {(profile.founder_names?.length || profile.ai_usage_declaration?.length) && (
                        <div className={styles.overviewMetaWrap}>
                          {profile.founder_names?.map((name, i) => (
                            <span key={`founder-${i}`} className={styles.metaPill}>{name}</span>
                          ))}
                          {profile.ai_usage_declaration?.slice(0, 4).map((item, i) => (
                            <span key={`usage-${i}`} className={styles.metaPillMuted}>{item}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {activeOverviewTab !== 'contacts' && (
                    <div className={styles.overviewPanelsGrid}>
                    {(activeOverviewTab === 'overview' || activeOverviewTab === 'business') && <div className={`${styles.profilePanelCard} ${activeOverviewTab === 'business' ? styles.profilePanelCardFull : ''}`}>
                      <span className={styles.overviewEyebrow}>Structure</span>
                      <h3>Business structure</h3>
                      <div className={styles.fieldGrid}>
                        <div className={styles.fieldCard}>
                          <span className={styles.fieldCardLabel}>Business Model</span>
                          <span className={styles.fieldCardValue}>{profile.business_model || '—'}</span>
                        </div>
                        <div className={styles.fieldCard}>
                          <span className={styles.fieldCardLabel}>Parent Company</span>
                          <span className={styles.fieldCardValue}>{profile.parent_company || '—'}</span>
                        </div>
                      </div>
                      {profile.subsidiary_companies?.length ? (
                        <div className={styles.fullField}>
                          <span className={styles.fullFieldLabel}>Subsidiaries</span>
                          <div className={styles.fieldTags}>
                            {profile.subsidiary_companies.map((sub, i) => (
                              <span key={i} className={styles.fieldTag}>{sub}</span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>}

                    {(activeOverviewTab === 'overview' || activeOverviewTab === 'compliance') && <div className={`${styles.profilePanelCard} ${activeOverviewTab === 'compliance' ? styles.profilePanelCardFull : ''}`}>
                      <span className={styles.overviewEyebrow}>Legal</span>
                      <h3>Registration &amp; IDs</h3>
                      <div className={styles.fieldGrid}>
                        <div className={styles.fieldCard}>
                          <span className={styles.fieldCardLabel}>MAC ID</span>
                          <span className={styles.fieldCardValue}>{profile.mac_id || '—'}</span>
                        </div>
                        <div className={styles.fieldCard}>
                          <span className={styles.fieldCardLabel}>GST Number</span>
                          <span className={styles.fieldCardValue}>{profile.gst_number || '—'}</span>
                        </div>
                      </div>
                    </div>}

                    {(activeOverviewTab === 'overview' || activeOverviewTab === 'compliance') && <div className={`${styles.profilePanelCard} ${styles.profilePanelCardFull}`}>
                      <span className={styles.overviewEyebrow}>Coverage</span>
                      <h3>AI, privacy and jurisdiction footprint</h3>
                      <div className={styles.fieldGrid}>
                        <div className={styles.fieldCard}>
                          <span className={styles.fieldCardLabel}>Headquarters</span>
                          <span className={styles.fieldCardValue}>{profile.headquarters_location || '—'}</span>
                        </div>
                        <div className={styles.fieldCard}>
                          <span className={styles.fieldCardLabel}>Processes Personal Data</span>
                          <span className={styles.fieldCardValue}>
                            {profile.processes_personal_data === null || profile.processes_personal_data === undefined ? '—' : profile.processes_personal_data ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>

                      <div className={styles.fullField}>
                        <span className={styles.fullFieldLabel}>AI Usage</span>
                        {profile.ai_usage_declaration?.length ? (
                          <div className={styles.fieldTags}>
                            {profile.ai_usage_declaration.map((v, i) => <span key={i} className={styles.fieldTag}>{v}</span>)}
                          </div>
                        ) : <span className={styles.fieldCardValue}>—</span>}
                      </div>

                      <div className={styles.fullField}>
                        <span className={styles.fullFieldLabel}>Regulatory Frameworks</span>
                        {profile.regulatory_declarations?.length ? (
                          <div className={styles.fieldTags}>
                            {profile.regulatory_declarations.map((v, i) => <span key={i} className={styles.fieldTag}>{v}</span>)}
                          </div>
                        ) : <span className={styles.fieldCardValue}>—</span>}
                      </div>

                      <div className={styles.fullField}>
                        <span className={styles.fullFieldLabel}>Jurisdictions</span>
                        {allJurisdictions.length ? (
                          <div className={styles.jurisdictionGrid}>
                            {allJurisdictions.map((j, i) => (
                              <div key={i} className={`${styles.jurisdictionItem} ${j.label === 'Active' ? styles.jurisdictionActive : styles.jurisdictionPlanned}`}>
                                <span className={styles.jFlag} role="img" aria-label={j.value}>{getFlag(j.value)}</span>
                                <span className={styles.jValue}>{cleanJurisName(j.value)}</span>
                                <span className={`${styles.jBadge} ${j.label === 'Active' ? styles.jActive : styles.jPlanned}`}>{j.label}</span>
                              </div>
                            ))}
                          </div>
                        ) : <span className={styles.fieldCardValue}>—</span>}
                      </div>
                    </div>}
                    </div>
                  )}

                  {activeOverviewTab === 'account' && (
                    <div className={styles.overviewPanelsGrid}>
                      <div className={`${styles.profilePanelCard} ${styles.profilePanelCardFull}`}>
                        <span className={styles.overviewEyebrow}>Subscription</span>
                        <h3>Account Details</h3>
                        <div className={styles.fieldGrid}>
                          <div className={styles.fieldCard}>
                            <span className={styles.fieldCardLabel}>Plan</span>
                            <span className={styles.fieldCardValue} style={{ textTransform: 'capitalize' }}>{subscription?.plan_tier || '—'}</span>
                          </div>
                          <div className={styles.fieldCard}>
                            <span className={styles.fieldCardLabel}>Billing cycle</span>
                            <span className={styles.fieldCardValue} style={{ textTransform: 'capitalize' }}>{subscription?.billing_cycle || '—'}</span>
                          </div>
                          <div className={styles.fieldCard}>
                            <span className={styles.fieldCardLabel}>Valid from</span>
                            <span className={styles.fieldCardValue}>{subscription?.current_period_start ? new Date(subscription.current_period_start).toLocaleDateString() : '—'}</span>
                          </div>
                          <div className={styles.fieldCard}>
                            <span className={styles.fieldCardLabel}>Valid until</span>
                            <span className={styles.fieldCardValue} style={{ color: daysUntilExpiry !== null && daysUntilExpiry <= 30 ? 'var(--ds-danger)' : undefined, fontWeight: daysUntilExpiry !== null && daysUntilExpiry <= 30 ? 600 : undefined }}>
                              {subEnd ? subEnd.toLocaleDateString() : '—'}
                              {daysUntilExpiry !== null && daysUntilExpiry > 0 && ` (${daysUntilExpiry}d left)`}
                              {daysUntilExpiry !== null && daysUntilExpiry <= 0 && ' (Expired)'}
                            </span>
                          </div>
                          <div className={styles.fieldCard}>
                            <span className={styles.fieldCardLabel}>Seats used</span>
                            <span className={styles.fieldCardValue}>
                              {seatsUsed}{profile?.max_seats != null ? ` / ${profile.max_seats}` : ''}
                              {profile?.max_seats != null && seatsUsed >= profile.max_seats && <span style={{ color: 'var(--ds-danger-text)', marginLeft: 6, fontSize: 12 }}>Limit reached</span>}
                            </span>
                          </div>
                          <div className={styles.fieldCard}>
                            <span className={styles.fieldCardLabel}>Status</span>
                            <span className={styles.fieldCardValue} style={{ textTransform: 'capitalize' }}>{subscription?.status || '—'}</span>
                          </div>
                        </div>

                        <div className={styles.fieldGrid} style={{ marginTop: 16 }}>
                          <div className={styles.fieldCard}>
                            <span className={styles.fieldCardLabel}>Amount paid</span>
                            <span className={styles.fieldCardValue}>{profile?.amount_paid != null ? `$${profile.amount_paid.toLocaleString()}` : '—'}</span>
                          </div>
                          <div className={styles.fieldCard}>
                            <span className={styles.fieldCardLabel}>Amount pending</span>
                            <span className={styles.fieldCardValue} style={{ color: profile?.amount_pending ? 'var(--ds-warning)' : undefined }}>{profile?.amount_pending != null ? `$${profile.amount_pending.toLocaleString()}` : '—'}</span>
                          </div>
                        </div>

                        {profile?.admin_notes && (
                          <div className={styles.fullField} style={{ marginTop: 16 }}>
                            <span className={styles.fullFieldLabel}>Notes from account manager</span>
                            <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--color-text-secondary, var(--ds-text-muted))', lineHeight: 1.6 }}>{profile.admin_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeOverviewTab === 'notifications' && (
                    <div className={styles.overviewPanelsGrid}>
                      <div className={`${styles.profilePanelCard} ${styles.profilePanelCardFull}`}>
                        <span className={styles.overviewEyebrow}>Alerts</span>
                        <h3>Notifications</h3>

                        {daysUntilExpiry === null && (
                          <p style={{ color: 'var(--color-text-secondary,var(--ds-text-muted))', fontSize: 14 }}>No active subscription found.</p>
                        )}

                        {daysUntilExpiry !== null && daysUntilExpiry <= 0 && (
                          <div style={{ background: 'var(--ds-danger-bg)', border: '1px solid var(--ds-danger-border)', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                              <span style={{ fontSize: 20, color: 'var(--ds-danger-text)', display: 'inline-flex' }}><AlertTriangle size={20} aria-hidden /></span>
                              <strong style={{ color: 'var(--ds-danger-text)' }}>Subscription Expired</strong>
                            </div>
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--ds-danger-text)' }}>Your subscription expired on {subEnd?.toLocaleDateString()}. Contact your account manager immediately to restore access.</p>
                          </div>
                        )}

                        {daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 7 && (
                          <div style={{ background: 'var(--ds-warning-bg)', border: '1px solid var(--ds-warning-bg)', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                              <span style={{ fontSize: 20, color: 'var(--ds-warning-text)', display: 'inline-flex' }}><AlertTriangle size={20} aria-hidden /></span>
                              <strong style={{ color: 'var(--ds-warning-text)' }}>Expiring in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}</strong>
                            </div>
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--ds-danger-text)' }}>Your subscription expires on {subEnd?.toLocaleDateString()}. Renew now to avoid service interruption.</p>
                          </div>
                        )}

                        {daysUntilExpiry !== null && daysUntilExpiry > 7 && daysUntilExpiry <= 30 && (
                          <div style={{ background: 'var(--ds-warning-bg)', border: '1px solid var(--ds-warning-bg)', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                              <span style={{ fontSize: 20, color: 'var(--ds-warning-text)', display: 'inline-flex' }}><Bell size={20} aria-hidden /></span>
                              <strong style={{ color: 'var(--ds-warning-text)' }}>Renewal reminder — {daysUntilExpiry} days left</strong>
                            </div>
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--ds-warning-text)' }}>Your subscription renews on {subEnd?.toLocaleDateString()}. Reach out to your account manager to discuss renewal terms.</p>
                          </div>
                        )}

                        {daysUntilExpiry !== null && daysUntilExpiry > 30 && (
                          <div style={{ background: 'var(--ds-success-bg)', border: '1px solid var(--ds-success)', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                              <span style={{ fontSize: 20, color: 'var(--ds-success-text)', display: 'inline-flex' }}><CheckCircle2 size={20} aria-hidden /></span>
                              <strong style={{ color: 'var(--ds-success-text)' }}>Subscription active</strong>
                            </div>
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--ds-success-text)' }}>Your subscription is active until {subEnd?.toLocaleDateString()} ({daysUntilExpiry} days remaining).</p>
                          </div>
                        )}

                        {profile?.max_seats != null && seatsUsed >= profile.max_seats && (
                          <div style={{ background: 'var(--ds-danger-bg)', border: '1px solid var(--ds-danger-border)', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                              <span style={{ fontSize: 20, color: 'var(--ds-danger-text)', display: 'inline-flex' }}><Ban size={20} aria-hidden /></span>
                              <strong style={{ color: 'var(--ds-danger-text)' }}>Seat limit reached ({seatsUsed}/{profile.max_seats})</strong>
                            </div>
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--ds-danger-text)' }}>You have used all available seats. Contact your account manager to add more seats.</p>
                          </div>
                        )}

                        {profile?.amount_pending != null && profile.amount_pending > 0 && (
                          <div style={{ background: 'var(--ds-warning-bg)', border: '1px solid var(--ds-warning-bg)', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                              <span style={{ fontSize: 20, color: 'var(--ds-warning-text)', display: 'inline-flex' }}><CreditCard size={20} aria-hidden /></span>
                              <strong style={{ color: 'var(--ds-warning-text)' }}>Payment pending — ${profile.amount_pending.toLocaleString()}</strong>
                            </div>
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--ds-danger-text)' }}>You have an outstanding balance. Please settle the payment to avoid any service disruptions.</p>
                          </div>
                        )}

                        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--color-border,var(--ds-border))' }}>
                          <h4 style={{ margin: '0 0 8px', fontSize: 14, color: 'var(--color-text,var(--ds-text))' }}>Contact your account manager</h4>
                          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--color-text-secondary,var(--ds-text-muted))' }}>For renewals, seat upgrades, billing queries, or custom contract terms — reach out directly.</p>
                          <a
                            href={`mailto:${contactEmail}?subject=Enterprise Account — ${profile?.company_name || 'Renewal Request'}`}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#101828', color: 'var(--ds-text-on-dark)', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}
                          >
                            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>
                            Contact Team to Renew
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeOverviewTab === 'contacts' && (
                    <div className={styles.overviewPanelsGrid}>
                      <div className={`${styles.profilePanelCard} ${styles.profilePanelCardFull}`}>
                        <span className={styles.overviewEyebrow}>Contacts</span>
                        <h3>People and ownership</h3>
                        {profile.contacts?.length ? (
                          <div className={styles.contactMiniList}>
                            {profile.contacts.map((c, i) => (
                              <div key={i} className={styles.contactMiniCard}>
                                <div className={styles.contactMiniAvatar}>{c.name.trim()[0]?.toUpperCase() ?? '?'}</div>
                                <div className={styles.contactMiniBody}>
                                  <span className={styles.contactMiniName}>{c.name}</span>
                                  <span className={styles.contactMiniMeta}>{c.role}{c.title ? ` · ${c.title}` : ''}</span>
                                  <span className={styles.contactMiniMeta}>{c.email}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className={styles.sideEmpty}>No contacts added yet.</p>
                        )}

                        {profile.founder_names?.length ? (
                          <div className={styles.fullField}>
                            <span className={styles.fullFieldLabel}>Founders</span>
                            <div className={styles.fieldTags}>
                              {profile.founder_names.map((name, i) => (
                                <span key={i} className={styles.fieldTag}>{name}</span>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>

                <aside className={styles.profileDashboardSide}>
                  <div className={styles.sideInfoCard}>
                    <span className={styles.overviewEyebrow}>Snapshot</span>
                    <h3>Company profile</h3>
                    <div className={styles.sideInfoList}>
                      {profileSnapshot.map((item) => (
                        <div key={item.label} className={styles.sideInfoRow}>
                          <span className={styles.sideInfoLabel}>{item.label}</span>
                          <span className={styles.sideInfoValue}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.sideInfoCard}>
                    <span className={styles.overviewEyebrow}>Contacts</span>
                    <h3>Key contacts</h3>
                    {profile.contacts?.length ? (
                      <div className={styles.contactMiniList}>
                        {profile.contacts.map((c, i) => (
                          <div key={i} className={styles.contactMiniCard}>
                            <div className={styles.contactMiniAvatar}>{c.name.trim()[0]?.toUpperCase() ?? '?'}</div>
                            <div className={styles.contactMiniBody}>
                              <span className={styles.contactMiniName}>{c.name}</span>
                              <span className={styles.contactMiniMeta}>{c.role}{c.title ? ` · ${c.title}` : ''}</span>
                              <span className={styles.contactMiniMeta}>{c.email}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={styles.sideEmpty}>No contacts added yet.</p>
                    )}
                  </div>
                </aside>
              </div>
            </div>
          ) : (
            // Consolidated editing form with better visual separation
            <div className={styles.consolidatedEditForm}>
              {/* Company Profile Section */}
              <div className={styles.editSection}>
                <div className={styles.editSectionHeader}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd"/></svg>
                  <span>Company Profile</span>
                </div>
                <div className={styles.editSectionContent}>
                  <div className={styles.bizFormField}>
                    <label className={styles.bizLabel}>Company Name</label>
                    <input className={styles.bizInput} value={infoName} onChange={e => setInfoName(e.target.value)} placeholder="Company name" />
                  </div>
                  <div className={styles.bizFormField}>
                    <label className={styles.bizLabel}>Industry</label>
                    <select className={styles.bizSelect} value={infoIndustry} onChange={e => setInfoIndustry(e.target.value)}>
                      <option value="">Select industry…</option>
                      {INDUSTRY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className={`${styles.bizFormField} ${styles.bizFormFieldFull}`}>
                    <label className={styles.bizLabel}>Primary Jurisdiction</label>
                    {/* Country selector */}
                    <select
                      className={styles.bizSelect}
                      value={jurisCountryId}
                      onChange={e => {
                        const opt = e.target.options[e.target.selectedIndex];
                        handleJurisCountryChange(e.target.value, opt.dataset.name || '');
                      }}
                    >
                      <option value="">Select country…</option>
                      {jurisCountries.map(c => (
                        <option key={c.id} value={c.id} data-name={c.name}>{c.name}</option>
                      ))}
                      <option value="__other__">Other (not listed)</option>
                    </select>

                    {/* Other country — free text */}
                    {jurisOtherCountry && (
                      <input
                        className={styles.bizInput}
                        style={{ marginTop: 8 }}
                        value={jurisCustomCountry}
                        onChange={e => {
                          const v = e.target.value;
                          setJurisCustomCountry(v);
                          setInfoJurisdiction([v, jurisCustomSub].filter(Boolean).join(' – '));
                        }}
                        placeholder="Enter country name"
                      />
                    )}

                    {/* Jurisdiction / state sub-picker */}
                    {!jurisOtherCountry && jurisCountryId && (
                      jurisSubLoading ? (
                        <p className={styles.bizHint} style={{ marginTop: 8 }}>Loading jurisdictions…</p>
                      ) : jurisSubList.length > 0 ? (
                        <select
                          className={styles.bizSelect}
                          style={{ marginTop: 8 }}
                          value={jurisSubId}
                          onChange={e => {
                            const opt = e.target.options[e.target.selectedIndex];
                            handleJurisSubChange(e.target.value, opt.dataset.name || '');
                          }}
                        >
                          <option value="">Select jurisdiction / state…</option>
                          {jurisSubList.map(j => (
                            <option key={j.id} value={j.id} data-name={j.name}>
                              {j.name}{j.type ? ` (${j.type})` : ''}
                            </option>
                          ))}
                          <option value="__other__">Other (not listed)</option>
                        </select>
                      ) : null
                    )}

                    {/* Other jurisdiction free text */}
                    {(jurisOtherSub || jurisOtherCountry) && (
                      <input
                        className={styles.bizInput}
                        style={{ marginTop: 8 }}
                        value={jurisCustomSub}
                        onChange={e => {
                          const v = e.target.value;
                          setJurisCustomSub(v);
                          const country = jurisOtherCountry ? jurisCustomCountry : jurisCountryName;
                          setInfoJurisdiction([country, v].filter(Boolean).join(' – '));
                        }}
                        placeholder="Enter jurisdiction / state / region"
                      />
                    )}

                    {/* Current resolved value preview */}
                    {infoJurisdiction ? (
                      <p className={styles.bizHint} style={{ marginTop: 6 }}>
                        Selected: <strong>{infoJurisdiction}</strong>
                      </p>
                    ) : (
                      <p className={styles.bizHint} style={{ marginTop: 6 }}>
                        Current: <strong>{profile?.jurisdiction || '—'}</strong> — select above to change
                      </p>
                    )}
                  </div>
                  <div className={styles.bizFormField}>
                    <label className={styles.bizLabel}>Years in Operation</label>
                    <input className={styles.bizInput} value={infoYears} onChange={e => setInfoYears(e.target.value)} placeholder="Years in operation" />
                  </div>
                  <div className={styles.bizFormField}>
                    <label className={styles.bizLabel}>Team Size</label>
                    <input className={styles.bizInput} value={infoMembers} onChange={e => setInfoMembers(e.target.value)} placeholder="Number of members" />
                  </div>
                  <div className={`${styles.bizFormField} ${styles.bizFormFieldFull}`}>
                    <label className={styles.bizLabel}>Products &amp; Services</label>
                    <input className={styles.bizInput} value={infoProducts} onChange={e => setInfoProducts(e.target.value)} placeholder="Products and services" />
                  </div>
                  <div className={styles.bizFormField}>
                    <label className={styles.bizLabel}>Company Size</label>
                    <select className={styles.bizSelect} value={infoCompanySize} onChange={e => setInfoCompanySize(e.target.value)}>
                      <option value="">Select size…</option>
                      {['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'].map(s => <option key={s} value={s}>{s} employees</option>)}
                    </select>
                  </div>
                  <div className={styles.bizFormField}>
                    <label className={styles.bizLabel}>Headquarters Location</label>
                    <input className={styles.bizInput} value={infoHeadquarters} onChange={e => setInfoHeadquarters(e.target.value)} placeholder="City, Country" />
                  </div>
                  <div className={styles.bizFormField}>
                    <label className={styles.bizLabel}>Processes Personal Data</label>
                    <select className={styles.bizSelect} value={infoProcessesPersonalData === null ? '' : String(infoProcessesPersonalData)} onChange={e => setInfoProcessesPersonalData(e.target.value === '' ? null : e.target.value === 'true')}>
                      <option value="">Not specified</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div className={`${styles.bizFormField} ${styles.bizFormFieldFull}`}>
                    <label className={styles.bizLabel}>AI Usage Declaration</label>
                    <div className={styles.tagCheckboxGroup}>
                      {['Build AI', 'Deploy AI', 'Use AI', 'Train models', 'Process data'].map(opt => (
                        <label key={opt} className={styles.tagCheckbox}>
                          <input type="checkbox" checked={infoAiUsage.includes(opt)} onChange={e => setInfoAiUsage(prev => e.target.checked ? [...prev, opt] : prev.filter(v => v !== opt))} />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className={`${styles.bizFormField} ${styles.bizFormFieldFull}`}>
                    <label className={styles.bizLabel}>Regulatory Frameworks</label>
                    <div className={styles.tagCheckboxGroup}>
                      {['GDPR', 'DPDP', 'CCPA', 'EU AI Act', 'ISO 27001', 'SOC 2', 'HIPAA', 'PCI DSS'].map(opt => (
                        <label key={opt} className={styles.tagCheckbox}>
                          <input type="checkbox" checked={infoRegulatoryDeclarations.includes(opt)} onChange={e => setInfoRegulatoryDeclarations(prev => e.target.checked ? [...prev, opt] : prev.filter(v => v !== opt))} />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className={`${styles.bizFormField} ${styles.bizFormFieldFull}`}>
                    <label className={styles.bizLabel}>Key Contacts</label>
                    {infoContacts.map((c, i) => (
                      <div key={i} className={styles.contactRow}>
                        <input className={styles.bizInput} placeholder="Name" value={c.name} onChange={e => setInfoContacts(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                        <input className={styles.bizInput} placeholder="Role (DPO, CAIO…)" value={c.role} onChange={e => setInfoContacts(prev => prev.map((x, j) => j === i ? { ...x, role: e.target.value } : x))} />
                        <input className={styles.bizInput} placeholder="Email" value={c.email} onChange={e => setInfoContacts(prev => prev.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} />
                        <button type="button" className={styles.removeBtn} onClick={() => setInfoContacts(prev => prev.filter((_, j) => j !== i))}>✕</button>
                      </div>
                    ))}
                    <button type="button" className={styles.addItemBtn} onClick={() => setInfoContacts(prev => [...prev, { role: '', name: '', email: '', title: '' }])}>+ Add contact</button>
                  </div>
                </div>
              </div>

              {/* Business Structure Section */}
              <div className={styles.editSection}>
                <div className={styles.editSectionHeader}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/></svg>
                  <span>Business Structure</span>
                </div>
                <div className={styles.editSectionContent}>
                  <div className={styles.bizFormField}>
                    <label className={styles.bizLabel}>Business Model</label>
                    <select className={styles.bizSelect} value={bizModel} onChange={e => setBizModel(e.target.value)}>
                      <option value="">Select model…</option>
                      {BUSINESS_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className={styles.bizFormField}>
                    <label className={styles.bizLabel}>Parent Company</label>
                    <input className={styles.bizInput} value={bizParent} onChange={e => setBizParent(e.target.value)} placeholder="Leave blank if this is the parent entity" />
                  </div>
                  <div className={`${styles.bizFormField} ${styles.bizFormFieldFull}`}>
                    <label className={styles.bizLabel}>Subsidiary Companies</label>
                    <div className={styles.bizTagRow}>
                      <input
                        className={styles.bizInput}
                        value={bizSubInput}
                        onChange={e => setBizSubInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); const v = bizSubInput.trim(); if (v) { setBizSubsidiaries(prev => [...prev, v]); setBizSubInput(''); } }
                        }}
                        placeholder="Type name and press Enter"
                      />
                      <button className={styles.bizAddBtn} type="button" onClick={() => { const v = bizSubInput.trim(); if (v) { setBizSubsidiaries(prev => [...prev, v]); setBizSubInput(''); } }}>Add</button>
                    </div>
                    <div className={styles.bizTags}>
                      {bizSubsidiaries.map((s, i) => (
                        <span key={i} className={styles.bizTag}>{s}<button type="button" onClick={() => setBizSubsidiaries(prev => prev.filter((_, idx) => idx !== i))}>×</button></span>
                      ))}
                    </div>
                  </div>
                  <div className={`${styles.bizFormField} ${styles.bizFormFieldFull}`}>
                    <label className={styles.bizLabel}>Company Hierarchy</label>
                    <p className={styles.bizHint}>Add related entities (branches, affiliates, JVs) to build your corporate tree.</p>
                    <div className={styles.bizTagRow}>
                      <input
                        className={styles.bizInput}
                        value={bizNodeName}
                        onChange={e => setBizNodeName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); const v = bizNodeName.trim(); if (v) { setBizHierarchy(prev => [...prev, { name: v, type: bizNodeType }]); setBizNodeName(''); } }
                        }}
                        placeholder="Entity name"
                        style={{ flex: 2 }}
                      />
                      <select className={styles.bizSelect} style={{ flex: 1 }} value={bizNodeType} onChange={e => setBizNodeType(e.target.value)}>
                        {HIERARCHY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <button className={styles.bizAddBtn} type="button" onClick={() => { const v = bizNodeName.trim(); if (v) { setBizHierarchy(prev => [...prev, { name: v, type: bizNodeType }]); setBizNodeName(''); } }}>Add</button>
                    </div>
                    {bizHierarchy.length > 0 && (
                      <div className={styles.hierarchyTree}>
                        <div className={styles.hierarchyRoot}>
                          <span className={styles.hierarchyRootLabel}>{profile.company_name || 'Your Company'}</span>
                        </div>
                        {bizHierarchy.map((node, i) => (
                          <div key={i} className={styles.hierarchyNode}>
                            <span className={styles.hierarchyLine}>└─</span>
                            <span className={styles.hierarchyNodeBadge}>{node.type}</span>
                            <span className={styles.hierarchyNodeName}>{node.name}</span>
                            <button type="button" className={styles.hierarchyRemove} onClick={() => setBizHierarchy(prev => prev.filter((_, idx) => idx !== i))}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Registration & Legal Section */}
              <div className={styles.editSection}>
                <div className={styles.editSectionHeader}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>
                  <span>Registration &amp; Legal</span>
                </div>
                <div className={styles.editSectionContent}>
                  <div className={styles.bizFormField}>
                    <label className={styles.bizLabel}>MAC ID</label>
                    <input className={styles.bizInput} value={regMacId} onChange={e => setRegMacId(e.target.value)} placeholder="MAC ID" />
                  </div>
                  <div className={styles.bizFormField}>
                    <label className={styles.bizLabel}>GST Number</label>
                    <input className={styles.bizInput} value={regGst} onChange={e => setRegGst(e.target.value)} placeholder="GST Number" />
                  </div>
                </div>
              </div>

              {/* Founders & Team Section */}
              <div className={styles.editSection}>
                <div className={styles.editSectionHeader}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/></svg>
                  <span>Founders &amp; Team</span>
                </div>
                <div className={styles.editSectionContent}>
                  <div className={`${styles.bizFormField} ${styles.bizFormFieldFull}`}>
                    <label className={styles.bizLabel}>Founder Names</label>
                    <div className={styles.bizTagRow}>
                      <input className={styles.bizInput} value={foundersInput} onChange={e => setFoundersInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const v = foundersInput.trim(); if (v) { setFoundersList(p => [...p, v]); setFoundersInput(''); } } }}
                        placeholder="Type name and press Enter" />
                      <button className={styles.bizAddBtn} type="button" onClick={() => { const v = foundersInput.trim(); if (v) { setFoundersList(p => [...p, v]); setFoundersInput(''); } }}>Add</button>
                    </div>
                    <div className={styles.bizTags}>
                      {foundersList.map((f, i) => (
                        <span key={i} className={styles.bizTag}>{f}<button type="button" onClick={() => setFoundersList(p => p.filter((_, idx) => idx !== i))}>×</button></span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Jurisdictions Section */}
              <div className={styles.editSection}>
                <div className={styles.editSectionHeader}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd"/></svg>
                  <span>Jurisdictions</span>
                </div>
                <div className={styles.editSectionContent}>
                  <div className={`${styles.bizFormField} ${styles.bizFormFieldFull}`}>
                    <JurisdictionPicker
                      label="Active Jurisdictions (already operating in)"
                      tags={jurisActive}
                      onAdd={v => setJurisActive(p => [...p, v])}
                      onRemove={i => setJurisActive(p => p.filter((_, idx) => idx !== i))}
                    />
                  </div>
                  <div className={`${styles.bizFormField} ${styles.bizFormFieldFull}`}>
                    <JurisdictionPicker
                      label="Planned Jurisdictions (to establish in)"
                      tags={jurisPlanned}
                      onAdd={v => setJurisPlanned(p => [...p, v])}
                      onRemove={i => setJurisPlanned(p => p.filter((_, idx) => idx !== i))}
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className={styles.editFormActions}>
                <button className={styles.bizCancelBtn} onClick={() => {
                  setInfoEditing(false);
                  setBizEditing(false);
                  setRegEditing(false);
                  setFoundersEditing(false);
                  setJurisEditing(false);
                }}>Cancel</button>
                <button className={styles.bizSaveBtn} onClick={async () => {
                  // Save all sections
                  await Promise.all([saveInfoEdit(), saveBizEdit(), saveRegEdit(), saveFoundersEdit(), saveJurisEdit()]);
                }} disabled={infoSaving || bizSaving || regSaving || foundersSaving || jurisSaving}>
                  {(infoSaving || bizSaving || regSaving || foundersSaving || jurisSaving) ? 'Saving…' : 'Save All'}
                </button>
              </div>
            </div>
          )}
        </div>


      </div>
    </div>
  );
}
